"use client"

import { useCallback, useEffect, useRef, useState } from "react"

import { deleteDraft, getDraft, putDraft, type IntakeFormDraftNav } from "@/lib/intake-form-drafts"
import {
  PublicIntakeFormError,
  type PublicIntakeFormBookingAttemptStatus,
  type PublicIntakeFormDraft,
  type PublicIntakeFormDraftDocument,
  type PublicIntakeFormDraftPayload,
  type PublicIntakeFormSchedulingAvailability,
  type PublicIntakeFormSubmissionPayload,
  type PublicIntakeFormSubmissionResult,
  type PublicIntakeFormUpload,
} from "@/hooks/api/public-intake-forms"

const DRAFT_DEBOUNCE_MS = 600
const DRAFT_RETRY_MS = 5000
const MAX_UPLOAD_FILES = 20
export type FormBridgeInitSource = "draft" | "prefill" | "merged" | "none"

export interface FormBridgeInit {
  answers: Record<string, unknown>
  attestations: Record<string, string>
  touchedKeys: string[]
  nav: IntakeFormDraftNav
  source: FormBridgeInitSource
  // Restored drafts carry decrypted sensitive values in answers; the bridge
  // must know their paths from the very first persist, not only after the
  // runtime's first answers_changed message.
  sensitivePaths: string[]
  documents?: PublicIntakeFormDraftDocument[]
  editingRole?: "recipient" | "advisor"
}

export type FormBridgeTerminalState = "version_gone" | "revoked" | null

export type FormBridgeSubmitStatus = "idle" | "uploading" | "submitting" | "confirming" | "succeeded" | "failed"

export interface FormBridgeSubmitState {
  status: FormBridgeSubmitStatus
  uploadedCount: number
  totalCount: number
}

export interface FormBridgeFrameProps {
  ref: React.RefObject<HTMLIFrameElement | null>
  onLoad: () => void
}

const BOOKING_POLL_INTERVAL_MS = 2500

interface UseFormBridgeOptions {
  localDraftKey?: string
  versionId: string
  init: FormBridgeInit | null
  uploadFile: (input: { fieldKey: string; clientSubmissionId: string; file: File }) => Promise<PublicIntakeFormUpload>
  submitForm: (submission: PublicIntakeFormSubmissionPayload) => Promise<PublicIntakeFormSubmissionResult>
  fetchSchedulingAvailability: (fieldKey: string) => Promise<PublicIntakeFormSchedulingAvailability>
  pollBookingAttempt: (input: {
    attemptId: string
    clientSubmissionId: string
  }) => Promise<PublicIntakeFormBookingAttemptStatus>
  remoteDraft?: PublicIntakeFormDraft | null
  saveDraft?: (draft: PublicIntakeFormDraftPayload) => Promise<PublicIntakeFormDraft>
  persistDocuments?: boolean
  draftSaveMode?: "automatic" | "manual"
}

interface RuntimeNav {
  active_step_key: string | null
  completed: Record<string, boolean>
  focus_key?: string | null
  focus_started?: boolean
}

interface RuntimeAnswersChanged {
  type: "runtime:answers_changed"
  answers: Record<string, unknown>
  attestations: Record<string, string>
  touched_keys: string[]
  nav: RuntimeNav
  sensitive_paths?: string[]
  documents?: { field_key: string; signed_id: string }[]
}

interface RuntimeSubmitStarted {
  type: "runtime:submit_started"
  request_id: string
  answers: Record<string, unknown>
  attestations: { field_key: string; typed_name: string }[]
  documents?: { field_key: string; signed_id: string }[]
  file_count: number
}

interface RuntimeDraftFileSelected {
  type: "runtime:draft_file_selected"
  file: RuntimeTransferredFile
}

interface RuntimeDraftUploadPending {
  type: "runtime:draft_upload_pending"
  pending: boolean
}

interface RuntimeFileReady {
  type: "runtime:file_ready"
  request_id: string
  file_index: number
  file: RuntimeTransferredFile
}

interface RuntimeTransferredFile {
  field_key: string
  client_key: string
  name: string
  type: string
  last_modified: number
  bytes: ArrayBuffer
}

interface RuntimeUploadFile {
  field_key: string
  client_key: string
  file: File
}

interface RuntimeSubmitRequested {
  type: "runtime:submit_requested"
  request_id: string
}

interface RuntimeSubmitCancelled {
  type: "runtime:submit_cancelled"
  request_id: string
}

interface RuntimeSchedulingAvailabilityRequested {
  type: "runtime:scheduling_availability_requested"
  request_id: string
  field_key: string
}

type RuntimeMessage =
  | { type: "runtime:ready" }
  | RuntimeAnswersChanged
  | RuntimeSubmitStarted
  | RuntimeFileReady
  | RuntimeSubmitRequested
  | RuntimeSubmitCancelled
  | RuntimeDraftFileSelected
  | RuntimeDraftUploadPending
  | RuntimeSchedulingAvailabilityRequested

interface DraftState {
  answers: Record<string, unknown>
  attestations: Record<string, string>
  touchedKeys: string[]
  nav: IntakeFormDraftNav
  sensitivePaths: string[]
  documents: { field_key: string; signed_id: string }[]
}

interface PendingSubmission {
  requestId: string
  answers: Record<string, unknown>
  attestations: { field_key: string; typed_name: string }[]
  documents: ({ field_key: string; signed_id: string } | null)[]
  persistedDocuments: { field_key: string; signed_id: string }[]
}

// The iframe has an opaque origin, so nothing about the message can be trusted
// beyond its shape — the sender is authenticated by window identity alone.
function parseRuntimeMessage(data: unknown): RuntimeMessage | null {
  if (typeof data !== "object" || data === null) return null
  const message = data as { type?: unknown }
  if (message.type === "runtime:ready") return { type: "runtime:ready" }
  if (message.type === "runtime:answers_changed") return data as RuntimeAnswersChanged
  if (message.type === "runtime:submit_started") return data as RuntimeSubmitStarted
  if (message.type === "runtime:file_ready") return data as RuntimeFileReady
  if (message.type === "runtime:submit_requested") return data as RuntimeSubmitRequested
  if (message.type === "runtime:submit_cancelled") return data as RuntimeSubmitCancelled
  if (message.type === "runtime:draft_file_selected") return data as RuntimeDraftFileSelected
  if (message.type === "runtime:draft_upload_pending") return data as RuntimeDraftUploadPending
  if (message.type === "runtime:scheduling_availability_requested")
    return data as RuntimeSchedulingAvailabilityRequested
  return null
}

function navFromRuntime(nav: RuntimeNav | undefined): IntakeFormDraftNav {
  return {
    activeStepKey: nav?.active_step_key ?? null,
    completed: nav?.completed ?? {},
    focusKey: nav?.focus_key ?? null,
    focusStarted: nav?.focus_started,
  }
}

function navToRuntime(nav: IntakeFormDraftNav): RuntimeNav {
  return {
    active_step_key: nav.activeStepKey,
    completed: nav.completed,
    focus_key: nav.focusKey ?? null,
    focus_started: nav.focusStarted,
  }
}

// Keyed by field so a cached signed id is only ever reused for the field it
// was minted for — the backend binds the credential to source, client
// submission id, and field key. The version prefix deliberately orphans cache
// entries persisted before the credential-purpose change; stale ids never
// match a lookup, so those files are re-uploaded instead of rejected at
// submit.
function fingerprintFile(fieldKey: string, file: File) {
  return `v2:${fieldKey}:${file.name}:${file.size}:${file.lastModified}`
}

function randomId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

class UploadFailure extends Error {
  constructor(
    public failedFieldKeys: string[],
    public reason: unknown
  ) {
    super("Upload failed")
    this.name = "UploadFailure"
  }
}

function materializeRuntimeFiles(files: RuntimeTransferredFile[]): RuntimeUploadFile[] {
  return files.map((entry) => {
    if (
      typeof entry !== "object" ||
      entry === null ||
      typeof entry.field_key !== "string" ||
      typeof entry.client_key !== "string" ||
      typeof entry.name !== "string" ||
      typeof entry.type !== "string" ||
      typeof entry.last_modified !== "number" ||
      !(entry.bytes instanceof ArrayBuffer)
    ) {
      const fieldKey =
        typeof entry === "object" && entry !== null && typeof entry.field_key === "string" ? entry.field_key : ""
      throw new UploadFailure(fieldKey ? [fieldKey] : [], new Error("Invalid file payload"))
    }

    return {
      field_key: entry.field_key,
      client_key: entry.client_key,
      file: new File([entry.bytes], entry.name, {
        type: entry.type,
        lastModified: entry.last_modified,
      }),
    }
  })
}

function terminalCodeFor(error: unknown): FormBridgeTerminalState {
  if (!(error instanceof PublicIntakeFormError)) return null
  if (error.status === 410) return "version_gone"
  if (error.status === 401 || error.status === 404) return "revoked"
  return null
}

function blockUnload(event: BeforeUnloadEvent) {
  event.preventDefault()
  event.returnValue = ""
}

function retryableDraftSaveError(error: unknown) {
  if (error instanceof PublicIntakeFormError) {
    return error.status === 408 || error.status === 429 || error.status >= 500
  }
  if (typeof error === "object" && error !== null && "status" in error && typeof error.status === "number") {
    return error.status === 408 || error.status === 429 || error.status >= 500
  }
  return true
}

export function useFormBridge({
  localDraftKey,
  versionId,
  init,
  uploadFile,
  submitForm,
  fetchSchedulingAvailability,
  pollBookingAttempt,
  remoteDraft,
  saveDraft,
  persistDocuments = false,
  draftSaveMode = "automatic",
}: UseFormBridgeOptions) {
  const frameRef = useRef<HTMLIFrameElement | null>(null)
  const initRef = useRef<FormBridgeInit | null>(init)
  const initSentRef = useRef(false)
  const draftStateRef = useRef<DraftState | null>(null)
  const clientSubmissionIdRef = useRef<string | null>(remoteDraft?.client_submission_id ?? null)
  const remoteDraftIdRef = useRef<string | null>(remoteDraft?.id ?? null)
  const uploadedBlobsRef = useRef<Record<string, string>>({})
  const pendingSubmissionRef = useRef<PendingSubmission | null>(null)
  const submittedRef = useRef(false)
  const dirtyRef = useRef(false)
  const activeRef = useRef(true)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const persistNowRef = useRef<() => Promise<void>>(async () => {})
  const remoteSaveInFlightRef = useRef(false)
  const uploadFileRef = useRef(uploadFile)
  const submitFormRef = useRef(submitForm)
  const fetchSchedulingAvailabilityRef = useRef(fetchSchedulingAvailability)
  const pollBookingAttemptRef = useRef(pollBookingAttempt)
  const saveDraftRef = useRef(saveDraft)

  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [hasPendingDraftUploads, setHasPendingDraftUploads] = useState(false)
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const [otherTabOpen, setOtherTabOpen] = useState(false)
  const [terminalState, setTerminalState] = useState<FormBridgeTerminalState>(null)
  const [submitState, setSubmitState] = useState<FormBridgeSubmitState>({
    status: "idle",
    uploadedCount: 0,
    totalCount: 0,
  })

  useEffect(() => {
    initRef.current = init
    uploadFileRef.current = uploadFile
    submitFormRef.current = submitForm
    fetchSchedulingAvailabilityRef.current = fetchSchedulingAvailability
    pollBookingAttemptRef.current = pollBookingAttempt
    saveDraftRef.current = saveDraft
  })

  useEffect(() => {
    if (!init || draftStateRef.current) return
    draftStateRef.current = {
      answers: init.answers,
      attestations: init.attestations,
      touchedKeys: init.touchedKeys,
      nav: init.nav,
      sensitivePaths: init.sensitivePaths,
      // The runtime needs the display metadata to rebuild persisted file rows
      // after a refresh; draft saves only consume field_key and signed_id.
      documents: init.documents ?? [],
    }
  }, [init])

  // A retried submission must reuse the identifiers minted by the attempt that
  // failed, so both survive a reload alongside the answers.
  useEffect(() => {
    if (!localDraftKey) return

    let cancelled = false
    void getDraft(localDraftKey).then((draft) => {
      if (cancelled || !draft) return
      clientSubmissionIdRef.current ??= draft.clientSubmissionId
      uploadedBlobsRef.current = { ...draft.uploadedBlobs, ...uploadedBlobsRef.current }
      setSavedAt(new Date(draft.updatedAt))
    })
    return () => {
      cancelled = true
    }
  }, [localDraftKey])

  const postToRuntime = useCallback((message: Record<string, unknown>) => {
    frameRef.current?.contentWindow?.postMessage(message, "*")
  }, [])

  const persistNow = useCallback(
    async (throwOnError = false) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
      const state = draftStateRef.current
      if (!state || !dirtyRef.current || submittedRef.current) return

      dirtyRef.current = false
      clientSubmissionIdRef.current ??= randomId()
      if (localDraftKey) {
        const saved = await putDraft(
          {
            key: localDraftKey,
            versionId,
            answers: state.answers,
            attestations: state.attestations,
            touchedKeys: state.touchedKeys,
            nav: state.nav,
            clientSubmissionId: clientSubmissionIdRef.current,
            uploadedBlobs: uploadedBlobsRef.current,
          },
          state.sensitivePaths
        )
        if (saved) setSavedAt(new Date(saved.updatedAt))
      }

      if (saveDraftRef.current) {
        if (remoteSaveInFlightRef.current) {
          dirtyRef.current = true
          return
        }

        remoteSaveInFlightRef.current = true
        let remoteSaved = false
        try {
          setIsSavingDraft(true)
          const remote = await saveDraftRef.current({
            id: remoteDraftIdRef.current,
            client_submission_id: clientSubmissionIdRef.current,
            answers: state.answers,
            documents: state.documents,
            attestations: state.attestations,
            touched_keys: state.touchedKeys,
            nav: navToRuntime(state.nav),
          })
          remoteDraftIdRef.current = remote.id
          clientSubmissionIdRef.current = remote.client_submission_id
          setSavedAt(new Date(remote.updated_at))
          remoteSaved = true
        } catch (error) {
          dirtyRef.current = true
          if (draftSaveMode === "automatic" && activeRef.current && retryableDraftSaveError(error)) {
            debounceRef.current = setTimeout(() => void persistNowRef.current(), DRAFT_RETRY_MS)
          }
          if (throwOnError) throw error
        } finally {
          setIsSavingDraft(false)
          remoteSaveInFlightRef.current = false
          if (remoteSaved && activeRef.current && dirtyRef.current && !debounceRef.current) {
            if (draftSaveMode === "automatic") {
              debounceRef.current = setTimeout(() => void persistNowRef.current(), 0)
            }
          } else if (remoteSaved) {
            setHasUnsavedChanges(false)
          }
        }
      }
    },
    [draftSaveMode, localDraftKey, versionId]
  )

  useEffect(() => {
    persistNowRef.current = persistNow
  }, [persistNow])

  const handleReady = useCallback(() => {
    if (initSentRef.current) return
    const payload = initRef.current
    if (!payload) return

    const state = draftStateRef.current
    initSentRef.current = true
    postToRuntime({
      type: "shell:init",
      answers: state?.answers ?? payload.answers,
      attestations: state?.attestations ?? payload.attestations,
      touched_keys: state?.touchedKeys ?? payload.touchedKeys,
      nav: navToRuntime(state?.nav ?? payload.nav),
      source: payload.source,
      editing_role: payload.editingRole ?? "recipient",
      documents: state?.documents ?? payload.documents ?? [],
      persist_documents: persistDocuments,
    })
  }, [persistDocuments, postToRuntime])

  const handleAnswersChanged = useCallback(
    (message: RuntimeAnswersChanged) => {
      if (submittedRef.current) return
      draftStateRef.current = {
        answers: message.answers ?? {},
        attestations: message.attestations ?? {},
        touchedKeys: Array.isArray(message.touched_keys) ? message.touched_keys : [],
        nav: navFromRuntime(message.nav),
        sensitivePaths: Array.isArray(message.sensitive_paths)
          ? message.sensitive_paths.filter((path) => typeof path === "string")
          : [],
        documents: Array.isArray(message.documents)
          ? message.documents.filter(
              (document) => typeof document.field_key === "string" && typeof document.signed_id === "string"
            )
          : [],
      }
      dirtyRef.current = true
      setHasUnsavedChanges(true)
      if (draftSaveMode === "manual") return
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => void persistNow(), DRAFT_DEBOUNCE_MS)
    },
    [draftSaveMode, persistNow]
  )

  const uploadOne = useCallback(
    async (entry: RuntimeUploadFile, persistAfterUpload = true) => {
      const fingerprint = fingerprintFile(entry.field_key, entry.file)
      const cached = uploadedBlobsRef.current[fingerprint]
      if (cached) return cached

      const upload = await uploadFileRef.current({
        fieldKey: entry.field_key,
        clientSubmissionId: clientSubmissionIdRef.current as string,
        file: entry.file,
      })
      uploadedBlobsRef.current = { ...uploadedBlobsRef.current, [fingerprint]: upload.signed_id }
      if (persistAfterUpload && draftSaveMode === "automatic") {
        dirtyRef.current = true
        await persistNow()
      }
      return upload.signed_id
    },
    [draftSaveMode, persistNow]
  )

  const handleDraftFileSelected = useCallback(
    async (message: RuntimeDraftFileSelected) => {
      try {
        clientSubmissionIdRef.current ??= randomId()
        const [file] = materializeRuntimeFiles([message.file])
        const signedId = await uploadOne(file, false)
        postToRuntime({
          type: "shell:draft_file_result",
          client_key: message.file.client_key,
          ok: true,
          signed_id: signedId,
        })
      } catch {
        postToRuntime({
          type: "shell:draft_file_result",
          client_key: message.file?.client_key,
          ok: false,
        })
      }
    },
    [postToRuntime, uploadOne]
  )

  const handleDraftUploadPending = useCallback((message: RuntimeDraftUploadPending) => {
    setHasPendingDraftUploads(message.pending === true)
    if (message.pending === true) setHasUnsavedChanges(true)
  }, [])

  // A 202 submission means the provider is confirming the booked slot; the shell
  // polls the attempt until it resolves. `failed` and `action_required` keep
  // the draft; success clears it exactly like a direct 201.
  const confirmBooking = useCallback(
    async (attemptId: string, requestId: string, totalCount: number) => {
      setSubmitState({ status: "confirming", uploadedCount: totalCount, totalCount })
      postToRuntime({
        type: "shell:submit_progress",
        request_id: requestId,
        stage: "confirming",
        uploaded_count: totalCount,
        total_count: totalCount,
      })

      for (;;) {
        await new Promise((resolve) => setTimeout(resolve, BOOKING_POLL_INTERVAL_MS))

        let attempt: PublicIntakeFormBookingAttemptStatus
        try {
          attempt = await pollBookingAttemptRef.current({
            attemptId,
            clientSubmissionId: clientSubmissionIdRef.current as string,
          })
        } catch (error) {
          const terminal = terminalCodeFor(error)
          if (terminal) {
            setSubmitState((state) => ({ ...state, status: "failed" }))
            setTerminalState(terminal)
            postToRuntime({ type: "shell:submit_result", request_id: requestId, ok: false, code: terminal })
            return
          }
          continue
        }

        if (attempt.status === "succeeded") {
          if (localDraftKey) await deleteDraft(localDraftKey)
          submittedRef.current = true
          dirtyRef.current = false
          setSubmitState({ status: "succeeded", uploadedCount: totalCount, totalCount })
          postToRuntime({ type: "shell:submit_result", request_id: requestId, ok: true })
          return
        }
        if (attempt.status === "failed" || attempt.status === "action_required") {
          setSubmitState((state) => ({ ...state, status: "failed" }))
          postToRuntime({
            type: "shell:submit_result",
            request_id: requestId,
            ok: false,
            code: "validation",
            errors: attempt.errors ?? [],
            field_errors: attempt.field_errors ?? {},
            booking_confirmed: attempt.booking_confirmed === true,
          })
          return
        }
      }
    },
    [localDraftKey, postToRuntime]
  )

  const handleSchedulingAvailabilityRequested = useCallback(
    async (message: RuntimeSchedulingAvailabilityRequested) => {
      try {
        const availability = await fetchSchedulingAvailabilityRef.current(message.field_key)
        const slots =
          availability.slots ??
          (availability.start_times ?? []).map((startTime) => ({ start_time: startTime, locations: [] }))
        const startTimes = availability.start_times ?? slots.map((slot) => slot.start_time)
        postToRuntime({
          type: "shell:scheduling_availability",
          request_id: message.request_id,
          ok: true,
          available: availability.available,
          slots,
          start_times: startTimes,
        })
      } catch (error) {
        postToRuntime({
          type: "shell:scheduling_availability",
          request_id: message.request_id,
          ok: false,
          error: error instanceof Error ? error.message : "unavailable",
        })
      }
    },
    [postToRuntime]
  )

  const handleSubmitStarted = useCallback(
    async (message: RuntimeSubmitStarted) => {
      const requestId = message.request_id
      const attestations = Array.isArray(message.attestations) ? message.attestations : []
      const fileCount = message.file_count
      if (!Number.isInteger(fileCount) || fileCount < 0 || fileCount > MAX_UPLOAD_FILES) {
        postToRuntime({ type: "shell:submit_ready", request_id: requestId, ok: false })
        postToRuntime({ type: "shell:submit_result", request_id: requestId, ok: false, code: "network" })
        return
      }

      // Freeze exactly what is being submitted before any network call, so a
      // crash mid-submit resumes on the submitted answers rather than older ones.
      draftStateRef.current = {
        answers: message.answers ?? {},
        attestations: Object.fromEntries(attestations.map((entry) => [entry.field_key, entry.typed_name])),
        touchedKeys: draftStateRef.current?.touchedKeys ?? [],
        nav: draftStateRef.current?.nav ?? { activeStepKey: null, completed: {} },
        sensitivePaths: draftStateRef.current?.sensitivePaths ?? [],
        documents: Array.isArray(message.documents) ? message.documents : [],
      }
      clientSubmissionIdRef.current ??= randomId()
      dirtyRef.current = true
      pendingSubmissionRef.current = {
        requestId,
        answers: message.answers ?? {},
        attestations,
        documents: Array.from({ length: fileCount }, () => null),
        persistedDocuments: Array.isArray(message.documents) ? message.documents : [],
      }
      window.addEventListener("beforeunload", blockUnload)
      setSubmitState({ status: "uploading", uploadedCount: 0, totalCount: fileCount })

      try {
        await persistNow()
        postToRuntime({ type: "shell:submit_ready", request_id: requestId, ok: true })
      } catch {
        pendingSubmissionRef.current = null
        window.removeEventListener("beforeunload", blockUnload)
        setSubmitState((state) => ({ ...state, status: "failed" }))
        postToRuntime({ type: "shell:submit_ready", request_id: requestId, ok: false })
        postToRuntime({ type: "shell:submit_result", request_id: requestId, ok: false, code: "network" })
      }
    },
    [persistNow, postToRuntime]
  )

  const handleFileReady = useCallback(
    async (message: RuntimeFileReady) => {
      const pending = pendingSubmissionRef.current
      const fileIndex = message.file_index
      if (
        !pending ||
        pending.requestId !== message.request_id ||
        !Number.isInteger(fileIndex) ||
        fileIndex < 0 ||
        fileIndex >= pending.documents.length
      ) {
        postToRuntime({
          type: "shell:file_result",
          request_id: message.request_id,
          file_index: fileIndex,
          ok: false,
        })
        postToRuntime({
          type: "shell:submit_result",
          request_id: message.request_id,
          ok: false,
          code: "network",
        })
        return
      }

      try {
        const [file] = materializeRuntimeFiles([message.file])
        const signedId = await uploadOne(file)
        pending.documents[fileIndex] = { field_key: file.field_key, signed_id: signedId }
        const uploadedCount = pending.documents.filter(Boolean).length
        setSubmitState({ status: "uploading", uploadedCount, totalCount: pending.documents.length })
        postToRuntime({
          type: "shell:submit_progress",
          request_id: pending.requestId,
          stage: "uploading",
          uploaded_count: uploadedCount,
          total_count: pending.documents.length,
        })
        postToRuntime({
          type: "shell:file_result",
          request_id: pending.requestId,
          file_index: fileIndex,
          ok: true,
        })
      } catch (error) {
        pendingSubmissionRef.current = null
        window.removeEventListener("beforeunload", blockUnload)
        setSubmitState((state) => ({ ...state, status: "failed" }))
        postToRuntime({
          type: "shell:file_result",
          request_id: pending.requestId,
          file_index: fileIndex,
          ok: false,
        })

        const terminal = terminalCodeFor(error)
        if (terminal) {
          setTerminalState(terminal)
          postToRuntime({ type: "shell:submit_result", request_id: pending.requestId, ok: false, code: terminal })
        } else {
          postToRuntime({
            type: "shell:submit_result",
            request_id: pending.requestId,
            ok: false,
            code: "upload_failed",
            failed_file_keys: typeof message.file?.field_key === "string" ? [message.file.field_key] : [],
          })
        }
      }
    },
    [postToRuntime, uploadOne]
  )

  const handleSubmitRequested = useCallback(
    async (message: RuntimeSubmitRequested) => {
      const pending = pendingSubmissionRef.current
      if (!pending || pending.requestId !== message.request_id || pending.documents.some((document) => !document)) {
        setSubmitState((state) => ({ ...state, status: "failed" }))
        postToRuntime({ type: "shell:submit_result", request_id: message.request_id, ok: false, code: "network" })
        window.removeEventListener("beforeunload", blockUnload)
        pendingSubmissionRef.current = null
        return
      }

      const { requestId, answers, attestations } = pending
      const documents = [
        ...pending.persistedDocuments,
        ...(pending.documents as { field_key: string; signed_id: string }[]),
      ]

      try {
        setSubmitState({ status: "submitting", uploadedCount: documents.length, totalCount: documents.length })
        postToRuntime({
          type: "shell:submit_progress",
          request_id: requestId,
          stage: "submitting",
          uploaded_count: documents.length,
          total_count: documents.length,
        })

        const result = await submitFormRef.current({
          client_submission_id: clientSubmissionIdRef.current as string,
          answers,
          documents,
          attestations,
        })

        if ("status" in result) {
          await confirmBooking(result.attempt_id, requestId, documents.length)
          return
        }

        if (localDraftKey) await deleteDraft(localDraftKey)
        submittedRef.current = true
        dirtyRef.current = false
        setSubmitState({ status: "succeeded", uploadedCount: documents.length, totalCount: documents.length })
        postToRuntime({ type: "shell:submit_result", request_id: requestId, ok: true })
      } catch (error) {
        const terminal = terminalCodeFor(error)
        setSubmitState((state) => ({ ...state, status: "failed" }))

        if (terminal) {
          setTerminalState(terminal)
          postToRuntime({ type: "shell:submit_result", request_id: requestId, ok: false, code: terminal })
        } else if (
          error instanceof PublicIntakeFormError &&
          (error.status === 422 || Object.keys(error.fieldErrors).length > 0)
        ) {
          postToRuntime({
            type: "shell:submit_result",
            request_id: requestId,
            ok: false,
            code: "validation",
            errors: error.errors,
            field_errors: error.fieldErrors,
          })
        } else {
          postToRuntime({ type: "shell:submit_result", request_id: requestId, ok: false, code: "network" })
        }
      } finally {
        window.removeEventListener("beforeunload", blockUnload)
        pendingSubmissionRef.current = null
      }
    },
    [confirmBooking, localDraftKey, postToRuntime]
  )

  const handleSubmitCancelled = useCallback((message: RuntimeSubmitCancelled) => {
    if (pendingSubmissionRef.current?.requestId !== message.request_id) return
    pendingSubmissionRef.current = null
    window.removeEventListener("beforeunload", blockUnload)
    setSubmitState({ status: "failed", uploadedCount: 0, totalCount: 0 })
  }, [])

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const frame = frameRef.current
      if (!frame || !frame.contentWindow || event.source !== frame.contentWindow) return

      const message = parseRuntimeMessage(event.data)
      if (!message) return
      if (message.type === "runtime:ready") handleReady()
      if (message.type === "runtime:answers_changed") handleAnswersChanged(message)
      if (message.type === "runtime:submit_started") void handleSubmitStarted(message)
      if (message.type === "runtime:file_ready") void handleFileReady(message)
      if (message.type === "runtime:submit_requested") void handleSubmitRequested(message)
      if (message.type === "runtime:submit_cancelled") handleSubmitCancelled(message)
      if (message.type === "runtime:draft_file_selected") void handleDraftFileSelected(message)
      if (message.type === "runtime:draft_upload_pending") handleDraftUploadPending(message)
      if (message.type === "runtime:scheduling_availability_requested")
        void handleSchedulingAvailabilityRequested(message)
    }

    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [
    handleAnswersChanged,
    handleFileReady,
    handleDraftFileSelected,
    handleDraftUploadPending,
    handleSchedulingAvailabilityRequested,
    handleReady,
    handleSubmitCancelled,
    handleSubmitRequested,
    handleSubmitStarted,
  ])

  useEffect(() => {
    activeRef.current = true
    if (draftSaveMode === "manual") {
      return () => {
        activeRef.current = false
      }
    }
    const flush = () => void persistNow()
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flush()
    }

    document.addEventListener("visibilitychange", onVisibilityChange)
    window.addEventListener("pagehide", flush)
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange)
      window.removeEventListener("pagehide", flush)
      flush()
      activeRef.current = false
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
    }
  }, [draftSaveMode, persistNow])

  // Two tabs on the same form both write the same draft key; last write wins and
  // the respondent gets a passive heads-up rather than a merge prompt.
  useEffect(() => {
    if (!localDraftKey) return
    if (typeof BroadcastChannel === "undefined") return

    let channel: BroadcastChannel | null = null
    try {
      channel = new BroadcastChannel(`intake-form:${localDraftKey}`)
    } catch {
      return
    }

    const openChannel = channel
    openChannel.onmessage = (event: MessageEvent) => {
      if (event.data === "hello") {
        setOtherTabOpen(true)
        openChannel.postMessage("here")
      } else if (event.data === "here") {
        setOtherTabOpen(true)
      }
    }
    openChannel.postMessage("hello")

    return () => openChannel.close()
  }, [localDraftKey])

  const clearDraft = useCallback(async () => {
    dirtyRef.current = false
    draftStateRef.current = null
    clientSubmissionIdRef.current = null
    uploadedBlobsRef.current = {}
    setSavedAt(null)
    setHasUnsavedChanges(false)
    if (localDraftKey) await deleteDraft(localDraftKey)
  }, [localDraftKey])

  const saveDraftNow = useCallback(async () => {
    await persistNow(true)
  }, [persistNow])

  const handleFrameLoad = useCallback(() => {
    initSentRef.current = false
  }, [])

  return {
    frameProps: { ref: frameRef, onLoad: handleFrameLoad } satisfies FormBridgeFrameProps,
    draftInfo: { savedAt, otherTabOpen },
    hasUnsavedChanges,
    hasPendingDraftUploads,
    isSavingDraft,
    saveDraftNow,
    clearDraft,
    submitState,
    terminalState,
  }
}
